import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Heart, Quote, Briefcase, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface Testimonial {
  id: string;
  title: string;
  story: string;
  role_achieved: string;
  company_name: string;
  is_anonymous: boolean;
  created_at: string;
}

export default function SuccessStories() {
  const { toast } = useToast();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      // Security: RLS policies ensure only approved testimonials are visible
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      setTestimonials(data || []);
    } catch (error: any) {
      console.error("Fetch error:", error);
      toast({
        title: "Error Loading Stories",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            Success Stories
          </CardTitle>
          <CardDescription>
            Real stories from people who found their second chance
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {testimonials.map((story) => (
          <Card key={story.id} className="relative overflow-hidden hover:shadow-lg transition-shadow animate-fade-in">
            <div className="absolute top-4 right-4 opacity-10">
              <Quote className="h-16 w-16" />
            </div>
            <CardHeader>
              <CardTitle className="text-lg">{story.title}</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                {story.role_achieved && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Briefcase className="h-3 w-3" />
                    {story.role_achieved}
                  </Badge>
                )}
                {story.company_name && (
                  <Badge variant="outline">{story.company_name}</Badge>
                )}
                {story.is_anonymous && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    Anonymous
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {story.story}
              </p>
              <div className="mt-4 pt-4 border-t flex items-center gap-2 text-xs text-muted-foreground">
                <Heart className="h-3 w-3" />
                Posted {new Date(story.created_at).toLocaleDateString("en-GB", {
                  month: "long",
                  year: "numeric",
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {testimonials.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              No success stories yet. Be the first to share your journey!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
